import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Syringe, Utensils, Scale, Pill, Activity, Clock } from "lucide-react";

const TEAL = "#0D9488";

const ICON = {
  shot: Syringe,
  meal: Utensils,
  weight: Scale,
  supplement: Pill,
};
const HUE = {
  shot: "text-teal-700 bg-teal-50",
  meal: "text-amber-700 bg-amber-50",
  weight: "text-sky-700 bg-sky-50",
  supplement: "text-fuchsia-700 bg-fuchsia-50",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

/** Live activity feed. Two modes:
 *    - clientId set → per-client feed shown in the client-overview panel
 *    - clientId omitted → cross-client feed for the coach dashboard
 */
export default function ClientActivityFeed({ clientId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    const url = clientId ? `/coach/clients/${clientId}/activity?limit=40` : `/coach/activity?limit=40`;
    setLoading(true);
    api.get(url)
      .then(({ data }) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // 20s polling
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={clientId ? "client-activity-feed" : "coach-activity-feed"}>
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4" style={{ color: TEAL }} />
        <h3 className="font-display font-bold text-slate-900">
          {clientId ? "Recent activity" : "All clients · live feed"}
        </h3>
        <span className="text-[10px] text-slate-400 tabular-nums ml-1">{events.length}</span>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-400 font-mono-num flex items-center gap-1">
          <Clock className="w-3 h-3" /> Auto-refresh 20s
        </span>
      </div>

      {loading && events.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Loading feed…</div>
      ) : events.length === 0 ? (
        <div className="text-xs text-slate-500 italic">
          {clientId ? "This client hasn't logged anything recently." : "Nothing to show yet — activity from your clients will appear here."}
        </div>
      ) : (
        <ul className="space-y-2 max-h-[420px] overflow-auto" data-testid={clientId ? "client-activity-list" : "coach-activity-list"}>
          {events.map((e, i) => {
            const Icon = ICON[e.type] || Activity;
            const hue = HUE[e.type] || "text-slate-700 bg-slate-50";
            return (
              <li
                key={`${e.type}-${i}-${e.at}`}
                className="flex items-start gap-3 border border-slate-100 rounded-md p-2.5 bg-slate-50/40"
                data-testid={`activity-row-${i}`}
              >
                <span className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${hue}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {e.client_handle && (
                      <span className="text-teal-700 font-mono-num text-[11px] mr-1.5">@{e.client_handle}</span>
                    )}
                    {e.label}
                  </div>
                  <div className="text-[12px] text-slate-500 truncate mt-0.5">{e.detail}</div>
                </div>
                <div className="text-[10px] text-slate-400 font-mono-num shrink-0 whitespace-nowrap">{timeAgo(e.at)}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
